import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../api';
import { displayLocation } from '../location';

// Warehouse → Units (docs/WAREHOUSE-MODEL.md): every pallet and metal bay the calibration
// walks found, the sections each carries, today's stock, and an OBSERVED capacity (per
// setup, the 90th percentile of pieces per section — until the pieces-per-box tests exist).
// Below: the numbering check per face (read-only). Claude, 2026-09-25.

const SETUP_LABEL: Record<string, string> = {
  S: 'S', M: 'M', SL: 'SL', LL: 'LL', ML: 'ML·S', tee: 'Tee', hoodie: 'Hoodie', tee_special_B: 'B-special', other: 'Other',
};

export default function UnitsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<any | null>(null);
  const [scheme, setScheme] = useState<any | null>(null);
  const [err, setErr] = useState('');
  const [row, setRow] = useState('');
  const [setup, setSetup] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [u, s] = await Promise.all([authFetch(`${API}/warehouse/units`), authFetch(`${API}/warehouse/scheme`)]);
        if (!u.ok || !s.ok) throw new Error(`HTTP ${u.status}/${s.status}`);
        setData(await u.json()); setScheme(await s.json());
      } catch (e: any) { setErr(e.message); }
    })();
  }, []);

  const units = useMemo(() => (data?.units || []).filter((u: any) =>
    (showInactive || u.active) && (!row || u.row_name === row) && (!setup || u.setup === setup)), [data, row, setup, showInactive]);
  const rows = useMemo(() => Array.from(new Set((data?.units || []).map((u: any) => u.row_name))).sort() as string[], [data]);
  const setups = useMemo(() => Array.from(new Set((data?.units || []).map((u: any) => u.setup).filter(Boolean))).sort() as string[], [data]);
  const notCalibrated = (data?.faces || []).filter((f: any) => f.sections > 0 && f.with_unit < f.sections).map((f: any) => f.letter);

  if (err) return <div className="p-6 text-red-400">{err}</div>;
  if (!data) return <div className="p-6 text-gray-500">Loading…</div>;

  return (
    <div className="p-6">
      <button onClick={() => navigate('/settings/warehouse/tools')} className="text-gray-400 hover:text-white text-sm">← Settings · Warehouse</button>
      <h1 className="text-xl font-semibold text-white mb-1">Units</h1>
      <p className="text-sm text-gray-400 mb-4">
        {data.units.filter((u: any) => u.active).length} units standing ·
        {notCalibrated.length ? ` faces with sections still to walk: ${notCalibrated.join(' ')}` : ' every face walked'}
      </p>
      <div className="flex gap-3 mb-3 text-sm">
        <select value={row} onChange={e => setRow(e.target.value)} className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white">
          <option value="">All rows</option>{rows.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={setup} onChange={e => setSetup(e.target.value)} className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white">
          <option value="">All setups</option>{setups.map(s => <option key={s} value={s}>{SETUP_LABEL[s] || s}</option>)}
        </select>
        <label className="text-gray-400 flex items-center gap-1">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> removed units too
        </label>
      </div>
      {units.length === 0 ? <div className="text-gray-500 text-sm">No units yet — walk an aisle in Warehouse → Calibrate.</div> : (
        <table className="w-full text-sm">
          <thead className="text-left text-gray-500 border-b border-gray-800">
            <tr><th className="py-2 pr-3">Unit</th><th className="pr-3">Row</th><th className="pr-3">Setup</th><th className="pr-3">Shelves</th><th className="pr-3">Sections</th>
                <th className="pr-3 text-right">Pieces</th><th className="pr-3 text-right">Observed capacity</th><th>Note</th></tr>
          </thead>
          <tbody>
            {units.map((u: any) => (
              <tr key={u.id} className={`border-b border-gray-800/60 ${u.active ? 'text-gray-200' : 'text-gray-600'}`}>
                <td className="py-1.5 pr-3 font-mono">{u.id}</td>
                <td className="pr-3">{u.row_name}{u.face ? ` · ${u.face}` : ''}</td>
                <td className="pr-3">{SETUP_LABEL[u.setup] || u.setup || '—'}{u.system ? ` · ${u.system}` : ''}{u.turn ? ` · ${u.turn}` : ''}</td>
                <td className="pr-3 text-gray-400">{u.kind === 'bay' ? <>
                  {u.shelves ?? '?'}{u.levels_cm?.length ? ` (${u.levels_cm.map((x: any) => x ?? '?').join('/')} cm)` : ''}
                  {u.system === 'R3000' && u.cross_bracing !== null ? ` · cross ${u.cross_bracing ? 'yes' : 'no'}` : ''}
                  {u.system === 'HI280' && u.beams !== null ? ` · ${u.beams} beams` : ''}</> : '—'}</td>
                <td className="pr-3" title={u.sections.map((s: any) => s.sid).join(', ')}>{u.sections.map((s: any) => displayLocation(s.code)).join(', ') || '—'}</td>
                <td className="pr-3 text-right">{u.pieces}</td>
                <td className="pr-3 text-right">{u.capacity_observed ? `${u.capacity_observed} (${Math.round(100 * u.pieces / u.capacity_observed)}%)` : '—'}</td>
                <td className="text-gray-400">{u.note || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2 className="text-lg font-semibold text-white mt-8 mb-2">Faces</h2>
      <table className="w-full text-sm mb-2">
        <thead className="text-left text-gray-500 border-b border-gray-800">
          <tr><th className="py-2 pr-3">Face</th><th className="pr-3">Aisle</th><th className="pr-3">Kind</th><th className="pr-3">Sections</th>
              <th className="pr-3">Units</th><th className="pr-3">Legs</th><th>Gaps</th></tr>
        </thead>
        <tbody>
          {(data.faces || []).map((f: any) => (
            <tr key={f.letter} className="border-b border-gray-800/60 text-gray-200 align-top">
              <td className="py-1.5 pr-3 font-semibold">{f.letter}</td>
              <td className="pr-3">{f.aisle}</td>
              <td className="pr-3">{f.kind === 'metal' ? f.system : 'pallets'}{f.measured_pitch_cm ? ` · ${f.measured_pitch_cm} cm/bay` : ''}</td>
              <td className="pr-3">{f.sections}{f.top_code ? ` (${displayLocation(f.top_code)} … ${displayLocation(f.bottom_code)})` : ''}</td>
              <td className="pr-3">{f.units} {f.with_unit < f.sections ? <span className="text-gray-500">({f.sections - f.with_unit} sections to walk)</span> : ''}</td>
              <td className="pr-3">{f.legs ? <>{f.legs.legs}{f.legs.extra_legs ? <span className="text-gray-500"> (+{f.legs.extra_legs} at {f.legs.breaks.map(displayLocation).join(', ')})</span> : ''}</> : '—'}</td>
              <td className="text-gray-400">{f.gaps.map((g: any) => `${g.after_code ? displayLocation(g.after_code) : '…'}|${g.before_code ? displayLocation(g.before_code) : 'top'} ${g.length_cm ?? '?'} cm ${g.reason}`).join(' · ') || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-gray-500 mb-6">Legs: neighbouring bays share a leg; a gap starts a new run, which needs one extra.</p>

      <h2 className="text-lg font-semibold text-white mt-8 mb-2">Numbering check</h2>
      <p className="text-sm text-gray-400 mb-2">
        Today's codes against each face's rule: the aisle's left letter counts up from the top wall, the right letter counts down.
        Read-only — renumbering is the move planner's job.
      </p>
      {scheme && Object.keys(scheme.faces).length === 0 && <div className="text-gray-500 text-sm">Nothing calibrated yet.</div>}
      {scheme && Object.entries(scheme.faces).map(([letter, f]: [string, any]) => (
        <div key={letter} className="text-sm mb-1.5">
          <b className="text-white">{letter}</b> <span className="text-gray-500">({f.aisle}, counts {f.numbering}, {f.codes.length} sections)</span>{' '}
          {f.findings.length === 0 ? <span className="text-emerald-300">follows the scheme</span>
            : <span className="text-gray-300">{f.findings.map((x: any) => x.text).join(' · ')}</span>}
        </div>
      ))}
    </div>
  );
}
